import { Fragment, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { store, useTables } from "../lib/store";
import { spacesSorted } from "../lib/select";
import { waitingOnMe } from "../lib/inbox";
import { navigate, spaceHref } from "../lib/nav";
import { setUi } from "../lib/keys";
import { api } from "../lib/api";
import { Avatar, Mark, SpaceDot } from "./primitives";
import { Icon } from "./icons";
import { MenuButton } from "./pickers";
import { NewSpaceDialog } from "./dialogs";
import { setTheme, theme } from "../lib/theme";
import { isAdmin, isContractor } from "../lib/access";

export function Sidebar() {
  const t = useTables();
  const [loc] = useLocation();
  const [newSpace, setNewSpace] = useState(false);
  const spaces = spacesSorted(t);
  const current = loc.startsWith("/s/") ? loc.split("/")[2] : null;
  const needs = waitingOnMe(t).count;
  const close = () => setUi({ nav: false });
  const here = (href: string) => (loc === href ? "page" : undefined);
  const online = store.presence.map((id) => t.actors.get(id)).filter((a) => a && a.id !== store.me?.id);

  return (
    <aside className="side" aria-label="Navigation">
      <div className="side-top">
        <Mark />
        <span>Melo</span>
        <span className="dom">melo.build</span>
      </div>
      <button type="button" className="search-btn" onClick={() => (setUi({ palette: true }), close())}>
        <Icon name="search" />
        <span>Search or jump</span>
        <span className="kbd">⌘K</span>
      </button>
      <nav className="nav" onClick={(e) => (e.target as HTMLElement).closest("a") && close()}>
        <Link href="/" className="nav-i" aria-current={here("/")}>
          <Icon name="home" />
          <span>Home</span>
          {needs > 0 && <span className="cnt sig">{needs}</span>}
        </Link>
        {!isContractor() && (
          <Link href="/people" className="nav-i" aria-current={loc === "/people" || loc === "/agents" ? "page" : undefined}>
            <Icon name="user" />
            <span>People</span>
          </Link>
        )}
        <div className="nav-h">
          <span>Projects</span>
          {isAdmin() && (
            <button type="button" onClick={() => setNewSpace(true)} aria-label="New project" title="New project">
              <Icon name="plus" size={14} />
            </button>
          )}
        </div>
        {spaces.map((s) => (
          <Link key={s.id} href={spaceHref(s.slug)} className="nav-i" aria-current={current === s.slug ? "page" : undefined}>
            <SpaceDot space={s} />
            <span>{s.name}</span>
          </Link>
        ))}
        {!spaces.length &&
          (isAdmin() ? (
            <button type="button" className="nav-i" onClick={() => setNewSpace(true)}>
              <Icon name="plus" />
              <span>Create your first project</span>
            </button>
          ) : (
            <p className="nav-empty">You're not on a project yet. An admin or producer can add you.</p>
          ))}
      </nav>
      <div className="side-foot">
        <span className={`conn ${store.connection}`} title={store.connection === "live" ? "Live" : store.connection === "offline" ? "Offline. Reconnecting." : "Connecting"} aria-label={store.connection === "live" ? "Live" : "Reconnecting"} role="status" />
        {online.length ? (
          <span className="presence" aria-label={`${online.length} others here`}>
            {online.slice(0, 5).map((a) => (
              <Avatar key={a!.id} actor={a} size="sm" />
            ))}
          </span>
        ) : (
          <span>{store.connection === "offline" ? "Reconnecting" : "Just you"}</span>
        )}
        <MenuButton
          label="Account"
          className="me-btn"
          align="end"
          items={() => [
            { id: "settings", label: "Settings and tokens", icon: <Icon name="key" />, run: () => navigate("/settings") },
            { id: "theme", label: theme() === "day" ? "Night theme" : "Day theme", icon: <Icon name={theme() === "day" ? "moon" : "sun"} />, run: () => setTheme(theme() === "day" ? "night" : "day") },
            { id: "out", label: "Sign out", icon: <Icon name="out" />, run: () => void api.post("/auth/signout").then(() => (location.href = "/signin")) },
          ]}
        >
          <Avatar actor={store.me ?? undefined} size="sm" />
        </MenuButton>
      </div>
      {newSpace && <NewSpaceDialog onClose={() => setNewSpace(false)} />}
    </aside>
  );
}

export function TopBar({ title, crumbs = [], right }: { title: string; crumbs?: { label: string; href: string }[]; right?: ReactNode }) {
  return (
    <header className="bar">
      <button type="button" className="btn ghost icon menu-toggle" onClick={() => setUi({ nav: true })} aria-label="Open navigation">
        <Icon name="menu" />
      </button>
      <div className="crumbs">
        {crumbs.map((c) => (
          <Fragment key={c.href}>
            <Link href={c.href}>{c.label}</Link>
            <Icon name="chev" />
          </Fragment>
        ))}
        <b>{title}</b>
      </div>
      {right && <div className="bar-r">{right}</div>}
    </header>
  );
}

/** People and the agents that work alongside them, as two tabs of one place. */
export function TeamTabs() {
  const [loc] = useLocation();
  const t = useTables();
  return (
    <div className="vrow">
      <nav className="vtabs" aria-label="Team">
        <Link href="/people" className="vtab" aria-current={loc === "/people" ? "page" : undefined}>
          <Icon name="user" />
          People
        </Link>
        <Link href="/agents" className="vtab" aria-current={loc === "/agents" ? "page" : undefined}>
          <Icon name="bot" />
          Agents
          {t.claims.size > 0 && <span className="cnt">{t.claims.size}</span>}
        </Link>
      </nav>
    </div>
  );
}
